import { useCallback, useContext, useState } from "react";
import { deleteVersion, getSingleVersion, rollbackVersion } from "../../../../../api/businessAndEnterprise/sheetApi";
import Context from "../../../../../context/Context";
import ReactNotifications from "../../../../common/reactNotifications/ReactNotifications";
import * as utilConstants from "../../../../../utils/constants/utilsConstants";
import DeleteModal from "../../../../common/commonModal/DeleteModal";

const DrawerMenu = ({ version, sheetId, versionId, setOpenDropdown, onHide, getAllVersions, handleEditSheet }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isRollbackConfirmation, setIsRollbackConfirmation] = useState(false);
    const [isDelVersionConf, setIsDelVersionConf] = useState(false);
    const { defaultColumns, defaultRow, setCurrentSheet,
        setValidSheetData, setAllComments, selectedSheetInfo, sheetRestoredDate } = useContext(Context);

    const viewSingleVersion = useCallback(() => {
        if (isLoading || !versionId) {
            console.log('Skipping call - loading:', isLoading, 'versionId:', versionId);
            return;
        }

        setIsLoading(true);
        getSingleVersion(sheetId, versionId, {}, (sheetResponse) => {

            if (sheetResponse?.status === true) {
                setOpenDropdown(null);
                onHide();

                sheetResponse?.data?.previous_data?.rows && setValidSheetData(sheetResponse?.data?.previous_data?.rows);
                const yearColumns =
                    sheetResponse?.data?.previous_data?.rows?.[0]?.time_frame_data?.map((item) => item.year) || [];
                const updatedColumns = [...defaultColumns.slice(0, -1), ...yearColumns];

                const maxRowId = Array.isArray(sheetResponse?.data?.previous_data?.rows)
                    ? Math.max(...sheetResponse.data.previous_data.rows.map((row) => row.row_id))
                    : 0;

                const updatedRows = Array.from({ length: maxRowId }, (_, i) => {
                    const rowData = sheetResponse?.data?.previous_data?.rows?.find((row) => parseInt(row.row_id) === i + 1) || null;
                    if (rowData) {
                        return [
                            rowData.identifier_code || "",
                            rowData.category_name || "",
                            rowData.category_code || "",
                            rowData.unit_name || "",
                            ...(rowData.time_frame_data?.map((item) => item.value || "") || []),
                        ];
                    }
                    return Array(updatedColumns.length).fill("");
                });

                setCurrentSheet({
                    name: selectedSheetInfo?.title,
                    data: [updatedColumns, ...updatedRows],
                });
                setAllComments(sheetResponse?.data?.previous_data?.cells);
            } else {
                setCurrentSheet({
                    name: selectedSheetInfo?.title,
                    data: [defaultColumns, defaultRow],
                });
                setAllComments([]);
            }

            setIsLoading(false);
        });
    }, [versionId, sheetId, isLoading, defaultColumns, defaultRow, setOpenDropdown, onHide, setValidSheetData, setCurrentSheet, setAllComments]);

    const handleRollback = async() => {
        if (versionId) {
           await rollbackVersion(sheetId, versionId, {}, (res) => {
                if (res?.status === true) {
                     handleEditSheet(selectedSheetInfo, sheetId, 'personal');
                    ReactNotifications(res?.message, utilConstants.sucessState200);
                    setIsRollbackConfirmation(false);
                    setOpenDropdown(null);
                onHide();
                } else {
                    console.log('error')
                    ReactNotifications(res?.data?.message, utilConstants.getErrorStatus);
                }
            })
        }
    };

    const handleDeleteVersion = () => {
        if (versionId) {
            deleteVersion(versionId, {}, (res) => {
                if (res?.status === true) {
                    ReactNotifications(res?.message, utilConstants.sucessState200);
                    setIsDelVersionConf(false);
                    getAllVersions();
                } else {
                    ReactNotifications(res?.data?.message, utilConstants.getErrorStatus);
                }
            })
        }
    }

    const handleCurrentViewClick = () => {
        handleEditSheet(selectedSheetInfo, sheetId, 'personal');
        setOpenDropdown(null);
        onHide();
    }



    return (
        <div className={`absolute right-5 ${(version?.id === 'current' || version.serial_number === 1) ? 'top-full' : 'bottom-full'} mb-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50`}>
            <button
                onClick={version?.id !== 'current' ? viewSingleVersion : handleCurrentViewClick}
                disabled={isLoading}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {isLoading ? 'Loading...' : 'View'}
            </button>
            {version?.id !== 'current' && (
                <>
                    <button onClick={() => setIsRollbackConfirmation(true)} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Restore version
                    </button>
                    <button onClick={() => setIsDelVersionConf(true)} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete version
                    </button>
                </>
            )}

            <DeleteModal
                show={isRollbackConfirmation}
                onClose={() => setIsRollbackConfirmation(false)}
                onConfirm={handleRollback}
                confirmText="Restore"
                title="Restore Version"
                message="Are you sure you want to restore this version? This action cannot be undone."
            />
            <DeleteModal
                show={isDelVersionConf}
                onClose={() => setIsDelVersionConf(false)}
                onConfirm={handleDeleteVersion}
                title="Delete Version"
                message="Are you sure you want to delete this version? This action cannot be undone."
            />
        </div>
    );
};

export default DrawerMenu;